// AI-CONTEXT: See docs/features/agenda-persoonskleur-en-huisbezoek-titel.md

import { getTaskTypeStyle } from '../taskTypeStyles'

/**
 * @typedef {object} AgendaColorOption
 * @property {string} key - Sleutel zoals hij op `Personas.AgendaColor` staat
 * @property {string} label - Wat de gebruiker leest bij het kiezen
 * @property {string} color - Randkleur van het blok (verzadigd)
 * @property {string} backgroundColor - Vlakkleur van het blok (licht)
 */

/**
 * @typedef {object} AgendaEventStyle
 * @property {string} color
 * @property {string} borderColor
 * @property {string} backgroundColor
 * @property {string} textColor
 */

/**
 * Dezelfde tekstkleur als in `taskTypeStyles.js`. Élke achtergrond hieronder
 * is licht genoeg om deze tekst met een contrast van minstens 4,5 te dragen;
 * `personaAgendaColors.test.js` rekent dat na.
 */
const TEXT_COLOR = '#151515'

/**
 * Alle kleuren die als persoonskleur opgeslagen kunnen zijn. Wat de kiezer
 * aanbiedt is `SELECTABLE_AGENDA_COLORS` — deze lijst is ruimer, want twee
 * kleuren zijn gereserveerd en staan al bij bestaande personas opgeslagen.
 *
 * Bewust géén vrije kleurkiezer: een zelfgekozen
 * donkerblauw met zwarte tekst is onleesbaar, en er is dan niets dat dat
 * tegenhoudt. De randkleuren wijken af van de soortkleuren in
 * `taskTypeStyles.js` (blauw voor huisbezoek, roze voor medewerker), zodat
 * een persoonskleur niet per ongeluk op een soort lijkt.
 *
 * @type {Array<AgendaColorOption>}
 */
export const AGENDA_COLOR_OPTIONS = [
    { key: 'rood', label: 'Rood', color: '#c0392b', backgroundColor: '#f5c6c2' },
    { key: 'oranje', label: 'Oranje', color: '#d35400', backgroundColor: '#fbd8bf' },
    { key: 'geel', label: 'Geel', color: '#b7950b', backgroundColor: '#fbf1b8' },
    { key: 'groen', label: 'Groen', color: '#27ae60', backgroundColor: '#c9f0d9' },
    { key: 'turkoois', label: 'Turkoois', color: '#16a085', backgroundColor: '#bfece3' },
    { key: 'blauw', label: 'Blauw', color: '#2e86c1', backgroundColor: '#c5e0f5' },
    { key: 'paars', label: 'Paars', color: '#8e44ad', backgroundColor: '#e2cdee' },
    { key: 'bruin', label: 'Bruin', color: '#8d6e63', backgroundColor: '#e6d8d3' },
    { key: 'grijs', label: 'Grijs', color: '#7f8c8d', backgroundColor: '#dfe4e5' },
]

/**
 * De agendastijl die bij een opgeslagen sleutel hoort, of `null` als er geen
 * (geldige) kleur gekozen is. Een onbekende sleutel — een oude naam, een
 * hexcode uit een eerdere poging — telt als 'geen kleur', zodat het blok
 * netjes op de soortkleur terugvalt in plaats van zwart te worden.
 * AI-CONTEXT: See docs/inbox.md bug #201 (pikzwarte grafiek zonder kleur)
 *
 * @param {string|null|undefined} key
 * @returns {AgendaEventStyle|null}
 */
export const resolveAgendaColorStyle = (key) => {
    if (typeof key !== 'string' || key === '') return null
    const option = AGENDA_COLOR_OPTIONS.find((o) => o.key === key)
    if (!option) return null
    return {
        color: option.color,
        borderColor: option.color,
        backgroundColor: option.backgroundColor,
        textColor: TEXT_COLOR,
    }
}

/**
 * De sleutel waarmee niet-ingeplande tijd gekleurd wordt. Benoemd in plaats van
 * los ingetypt: `UNPLANNED_TIME_STYLE` hangt eraan, en haalt iemand 'grijs'
 * later uit de lijst hierboven — plausibel, het is immers geen keuze meer —
 * dan staat dat hier, in plaats van dat de agenda stil op `null` uitkomt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @type {string}
 */
const UNPLANNED_TIME_KEY = 'grijs'

/**
 * Het paars uit de keuzelijst. Niet de kleur van een gezamenlijke afspraak (die
 * staat los, zie `SHARED_APPOINTMENT_STYLE`) maar wél gereserveerd: twee bijna
 * gelijke paarsen naast elkaar in één agenda is erger dan één paars minder.
 *
 * @type {string}
 */
const PERSONA_PURPLE_KEY = 'paars'

/**
 * Twee kleuren dragen sinds september 2026 een eigen betekenis in de agenda en
 * zijn daarom niet meer als persoonskleur te kiezen: grijs is niet-ingeplande
 * tijd, paars is een afspraak met meer dan één persoon. Wie er al een had houdt
 * hem — `resolveAgendaColorStyle` kent ze gewoon nog — maar dan valt zijn
 * afspraak visueel samen met een van deze twee.
 * AI-CONTEXT: See docs/features/agenda-persoonskleur-en-huisbezoek-titel.md
 *
 * @type {Array<string>}
 */
export const RESERVED_AGENDA_COLOR_KEYS = [UNPLANNED_TIME_KEY, PERSONA_PURPLE_KEY]

/**
 * De kleuren die de kleurkiezer aanbiedt: de volledige lijst min de twee
 * hierboven. Afgeleid en niet overgeschreven, zodat er niets uit de pas kan
 * gaan lopen. Zie docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
 *
 * @type {Array<AgendaColorOption>}
 */
export const SELECTABLE_AGENDA_COLORS = AGENDA_COLOR_OPTIONS
    .filter((option) => !RESERVED_AGENDA_COLOR_KEYS.includes(option.key))

/**
 * Niet-ingeplande tijd: het roosterblok van een medewerker, of wat daarvan
 * overblijft nadat de afspraken eruit geknipt zijn. Grijs op verzoek van de
 * FIXbrigades — de kleur van een medewerker hoort te betekenen "hier is hij
 * ergens voor ingepland", en vrije tijd hoort weg te vallen.
 *
 * @type {AgendaEventStyle}
 */
export const UNPLANNED_TIME_STYLE = resolveAgendaColorStyle(UNPLANNED_TIME_KEY)

/**
 * Een afspraak waar meer dan één persoon op staat. Eén blok kan niet twee
 * persoonskleuren dragen, dus krijgt het een eigen kleur in plaats van terug
 * te vallen op de soortkleur — dan was het niet van een gewone afspraak te
 * onderscheiden.
 *
 * Een eigen hexcode en niet het 'paars' uit de keuzelijst (`#8e44ad`): dat zit
 * maar 18 graden in kleurtoon van de soortkleur van een meetmoment (`#a400a4`),
 * en in de week- en dagweergave draagt alleen een randje van 5px de kleur (zie
 * `src/CSS/layout/calendar.css`). Dit blauwere paars zit op 32 graden.
 * `personaAgendaColors.test.js` rekent dat na tegen elke soortkleur, zodat een
 * nieuwe soortkleur er niet ongemerkt tegenaan kruipt.
 *
 * @type {AgendaEventStyle}
 */
export const SHARED_APPOINTMENT_STYLE = {
    color: '#5b2c8f',
    borderColor: '#5b2c8f',
    backgroundColor: '#d9cdee',
    textColor: TEXT_COLOR,
}

/**
 * Persona-ID → agendastijl, alleen voor personas mét een geldige kleur.
 *
 * @param {Array<{ID?: string, AgendaColor?: string}>|null|undefined} personas
 * @returns {Record<string, AgendaEventStyle>}
 */
export const personaColorById = (personas) => {
    if (!Array.isArray(personas)) return {}
    return personas.reduce((map, persona) => {
        if (!persona?.ID) return map
        const style = resolveAgendaColorStyle(persona.AgendaColor)
        if (style) map[persona.ID] = style
        return map
    }, {})
}

/**
 * Welke kleur een agendablok krijgt. Drie regels, in deze volgorde:
 *
 * 1. Een roosterblok (`personaSchedule`) is niet-ingeplande tijd en wordt
 *    grijs — ook als de medewerker een eigen kleur heeft. Zijn naam staat als
 *    titel in het blok, dus je ziet nog steeds van wie de vrije tijd is.
 * 2. Staan er twee of meer mensen op de afspraak, dan wordt hij paars. Eén blok
 *    kan niet twee persoonskleuren dragen.
 * 3. Staat er precies één persoon op, dan krijgt het blok diens kleur; heeft
 *    die niets gekozen, dan de soortkleur.
 *
 * @param {{Type?: string, TeamMembers?: Array<string>}|null|undefined} task - `PersonaID` wordt bewust niet gelezen; een roosterblok is altijd grijs
 * @param {Record<string, AgendaEventStyle>} personaColors - Uitkomst van personaColorById
 * @param {AgendaEventStyle} fallback - De soortkleur uit getTaskTypeStyle
 * @returns {AgendaEventStyle}
 */
export const agendaEventStyle = (task, personaColors, fallback) => {
    if (!task) return fallback

    // `|| fallback` want een ontbrekende sleutel mag hier geen `null` opleveren:
    // CalendarView leest er meteen `.color` van. Zie docs/inbox.md bug #201.
    if (task.Type === 'personaSchedule') return UNPLANNED_TIME_STYLE || fallback

    const members = Array.isArray(task.TeamMembers) ? task.TeamMembers : []
    if (members.length > 1) return SHARED_APPOINTMENT_STYLE
    if (members.length === 0) return fallback

    return (personaColors && personaColors[members[0]]) || fallback
}

/**
 * De kleur waarmee een soort in de filterrij van de agenda getoond wordt.
 *
 * De pillen- en chipsrij is het dichtst bij een legenda dat de agenda heeft, dus
 * hij moet zeggen wat de blokken doen. Voor 'Medewerker' is dat sinds
 * 2026-09-17 niet meer de soortkleur (roze) maar grijs: er bestaat geen roze
 * roosterblok meer. Zonder deze helper vertellen de twee los van elkaar wat een
 * roosterblok is, en ziet niets de tegenspraak.
 * AI-CONTEXT: See docs/pitfalls.md#ui-independent-badges-contradict-each-other
 *
 * @param {string} type - Waarde uit TASK_TYPE_STYLES
 * @returns {AgendaEventStyle} Altijd een stijl, nooit null
 */
export const agendaTypeLegendStyle = (type) => {
    const fallback = getTaskTypeStyle(type)
    if (type === 'personaSchedule') return UNPLANNED_TIME_STYLE || fallback
    return fallback
}
